import Swal from 'sweetalert2';

// Constantes de estilo
const STYLES = {
  errorCard: "display:flex;align-items:center;padding:8px;margin:4px 0;background:#f8f9fa;border-left:3px solid #dc3545;border-radius:4px;",
  errorLabel: "font-weight:600;color:#dc3545;min-width:60px;font-size:13px;",
  errorText: "color:#495057;font-size:13px;",
  successBadge: "display:inline-block;padding:12px 20px;background:#d4edda;border-radius:6px;",
  successText: "font-weight:600;color:#155724;font-size:16px;",
  partialSuccessBadge: "display:inline-block;padding:8px 16px;background:#d4edda;border-radius:6px;margin:4px;",
  partialSuccessText: "font-weight:600;color:#155724;font-size:14px;",
  partialErrorBadge: "display:inline-block;padding:8px 16px;background:#f8d7da;border-radius:6px;margin:4px;",
  partialErrorText: "font-weight:600;color:#721c24;font-size:14px;",
  errorListContainer: "max-height:320px;overflow-y:auto;padding:8px;",
  sectionTitle: "margin:16px 0 12px 0;font-weight:600;color:#343a40;font-size:15px;",
  description: "margin-bottom:16px;color:#6c757d;font-size:14px;"
};

const BUTTON_COLOR = '#6c757d';

// Devuelve el nombre en singular o plural según la cantidad
const getEntidadLabel = (cantidad, entidad) => {
  // Si la cantidad es 1, devolver singular (sin la "s" final)
  if (cantidad === 1) {
    return entidad.endsWith('s') ? entidad.slice(0, -1) : entidad;
  }
  // Si es mayor a 1, devolver plural (con la "s")
  return entidad;
};

// Formatea los errores para mostrarlos en el modal
const formatErrors = (errorsObj, entidad) => {
  return Object.entries(errorsObj)
    .filter(([key]) => key.startsWith('error_fila_') || key.startsWith('fila_'))
    .sort(([keyA], [keyB]) => {
      // Ordenar por número de fila
      const numA = parseInt(keyA.replace(/^(error_)?fila_/, '')) || 0;
      const numB = parseInt(keyB.replace(/^(error_)?fila_/, '')) || 0;
      return numA - numB;
    })
    .map(([fila, mensaje]) => {
      // Maneja tanto 'error_fila_X' como 'fila_X'
      const numeroFila = fila.replace(/^(error_)?fila_/, '');
      // Reemplaza el nombre de la entidad en el mensaje por singular/plural según corresponda
      let mensajeFormateado = mensaje.replace(/articulos|artículo/gi, getEntidadLabel(1, entidad));
      return `
      <div style="${STYLES.errorCard}">
        <span style="${STYLES.errorLabel}">Fila ${numeroFila}</span>
        <span style="${STYLES.errorText}">${mensajeFormateado}</span>
      </div>
    `;
    });
};

// Modal para cuando todos los registros se importaron correctamente
const showSuccessResult = (success, entidad) => {
  const label = getEntidadLabel(success, entidad);
  Swal.fire({
    title: 'Importación exitosa',
    html: `
      <div style="margin-bottom:16px;">
        <div style="${STYLES.successBadge}">
          <span style="${STYLES.successText}">✓ ${success} ${label} importado${success === 1 ? '' : 's'} correctamente</span>
        </div>
      </div>
    `,
    icon: 'success',
    width: 550,
    confirmButtonText: 'Cerrar',
    confirmButtonColor: BUTTON_COLOR
  });
};

// Modal para cuando ningún registro se pudo importar
const showFailedResult = (failed, errores, entidad) => {
  const label = getEntidadLabel(1, entidad); // Siempre singular para "Ningún"
  Swal.fire({
    title: 'Importación fallida',
    html: `
      <div style="${STYLES.description}">
        Ningún ${label} se pudo importar.
      </div>
      <div style="margin-bottom:12px;font-weight:600;color:#343a40;font-size:15px;">
        ${failed} ${getEntidadLabel(failed, entidad)} con error${failed === 1 ? '' : 'es'}:
      </div>
      <div style="${STYLES.errorListContainer}">
        ${errores.join('')}
      </div>
    `,
    icon: 'error',
    width: 650,
    confirmButtonText: 'Cerrar',
    confirmButtonColor: BUTTON_COLOR
  });
};

// Modal para cuando algunos se importaron y otros tuvieron errores
const showPartialResult = (success, failed, errores, entidad) => {
  const labelSuccess = getEntidadLabel(success, entidad);
  const labelFailed = getEntidadLabel(failed, entidad);
  Swal.fire({
    title: 'Importación parcial',
    html: `
      <div style="margin-bottom:16px;">
        <div style="${STYLES.partialSuccessBadge}">
          <span style="${STYLES.partialSuccessText}">✓ ${success} ${labelSuccess} importado${success === 1 ? '' : 's'}</span>
        </div>
        <div style="${STYLES.partialErrorBadge}">
          <span style="${STYLES.partialErrorText}">✗ ${failed} ${labelFailed} con error${failed === 1 ? '' : 'es'}</span>
        </div>
      </div>
      <div style="${STYLES.sectionTitle}">
        Detalle de errores:
      </div>
      <div style="${STYLES.errorListContainer}">
        ${errores.join('')}
      </div>
    `,
    icon: 'warning',
    width: 650,
    confirmButtonText: 'Cerrar',
    confirmButtonColor: BUTTON_COLOR
  });
};

// Modal para cuando no hay datos en la respuesta
const showNoDataResult = () => {
  Swal.fire({
    title: 'Sin datos',
    text: 'No se recibieron datos de la importación.',
    icon: 'info',
    confirmButtonText: 'Cerrar',
    confirmButtonColor: BUTTON_COLOR
  });
};

// Muestra un error general cuando falla la petición de importación
export const showImportError = (entidad = 'artículos') => {
  const label = getEntidadLabel(2, entidad);
  Swal.fire({
    title: 'Error de importación',
    text: `No se pudo importar los ${label}. Verifique los datos de su archivo.`,
    type: 'error',
    confirmButtonText: 'Cerrar',
    confirmButtonColor: BUTTON_COLOR,
    width: 500
  });
};

// Función principal que muestra el resultado de la importación
// recibe la respuesta del servidor y el nombre de la entidad (ej: 'artículos', 'stocks', etc)
function showImportResult(data, entidad = 'artículos') {
  const response = data.data || data;

  // Detectar si el formato es el nuevo (con error_fila_X) o el antiguo (con success/failed/errors)
  const hasErrorFilaFormat = Object.keys(response).some(key => key.startsWith('error_fila_'));

  let success = 0;
  let failed = 0;
  let errors = {};

  if (hasErrorFilaFormat) {
    // Formato nuevo: extraer errores de error_fila_X
    errors = Object.keys(response)
      .filter(key => key.startsWith('error_fila_') || key.startsWith('fila_'))
      .reduce((acc, key) => {
        acc[key] = response[key];
        return acc;
      }, {});

    failed = Object.keys(errors).length;

    // Si hay mensaje pero no errores, asumimos éxito
    if (failed === 0 && response.mensaje) {
      // Intentar extraer el número de éxitos del mensaje si es posible
      // Por ahora, mostramos éxito genérico
      success = 1; // Se puede mejorar si el backend envía el número
    }
  } else {
    // Formato antiguo: success, failed, errors
    success = response.success || 0;
    failed = response.failed || 0;
    errors = response.errors || {};

    // También buscar errores en formato error_fila_X dentro de errors
    if (errors && typeof errors === 'object' && errors !== null) {
      const errorKeys = Object.keys(errors);
      const hasErrorFilaInErrors = errorKeys.some(key => key.startsWith('error_fila_') || key.startsWith('fila_'));

      if (hasErrorFilaInErrors) {
        // Si errors contiene error_fila_X, usarlos directamente
        const errorFilaErrors = errorKeys
          .filter(key => key.startsWith('error_fila_') || key.startsWith('fila_'))
          .reduce((acc, key) => {
            acc[key] = errors[key];
            return acc;
          }, {});
        if (Object.keys(errorFilaErrors).length > 0) {
          errors = errorFilaErrors;
          failed = Object.keys(errors).length;
        }
      }
    }

    // Verificar si hay errores en el nivel raíz del response (por si el backend cambió el formato)
    const responseKeys = Object.keys(response);
    const hasErrorFilaInRoot = responseKeys.some(key => key.startsWith('error_fila_'));

    if (hasErrorFilaInRoot && Object.keys(errors).length === 0) {
      // Si no hay errores en errors pero sí en el nivel raíz, usarlos
      const rootErrors = responseKeys
        .filter(key => key.startsWith('error_fila_') || key.startsWith('fila_'))
        .reduce((acc, key) => {
          acc[key] = response[key];
          return acc;
        }, {});
      if (Object.keys(rootErrors).length > 0) {
        errors = rootErrors;
        failed = Object.keys(errors).length;
      }
    }
  }

  const errores = formatErrors(errors, entidad);

  // Determinar qué modal mostrar según los resultados
  if (failed === 0) {
    // No hay errores - mostrar éxito
    if (hasErrorFilaFormat && response.mensaje) {
      // Formato nuevo: mostrar mensaje del servidor
      Swal.fire({
        title: 'Importación exitosa',
        text: response.mensaje,
        icon: 'success',
        width: 550,
        confirmButtonText: 'Cerrar',
        confirmButtonColor: BUTTON_COLOR
      });
    } else if (success > 0) {
      // Formato antiguo: mostrar con cantidad
      showSuccessResult(success, entidad);
    } else {
      // Sin datos específicos, mostrar mensaje genérico
      Swal.fire({
        title: 'Importación exitosa',
        text: `Se importaron los ${getEntidadLabel(2, entidad)} correctamente.`,
        icon: 'success',
        width: 550,
        confirmButtonText: 'Cerrar',
        confirmButtonColor: BUTTON_COLOR
      });
    }
  } else if (failed > 0 && success === 0) {
    // Solo hay errores
    showFailedResult(failed, errores, entidad);
  } else if (success > 0 && failed > 0) {
    // Hay éxitos y errores
    showPartialResult(success, failed, errores, entidad);
  } else {
    // Sin datos
    showNoDataResult();
  }
}

export default showImportResult;